import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { makeAutoObservable } from 'mobx'
import { MDBBtn, MDBRow, MDBCol, MDBBtnGroup, MDBContainer } from 'mdb-react-ui-kit'
import { UITypeAhead, UISelect, UIInput, UIInputToggleSwitch } from './../../components/forms/form-fields'
import UserProfileService from './../../shared/services/UserProfile.service'
import UsertypeService from './../../shared/services/Usertype.service'
import NotificationService from './../../shared/services/Notification.service'
import { toast } from 'react-toastify'
import AdminApi from './../../api/admin-api/admin-api'
import { commissionLevels } from '../../constants/commisionLevels'
import AuthService from './../../shared/services/Auth.service'
import { buildEnrollmentUrl } from './../../shared/utilities/enrollmentUrl.function'
import { isWhiteLabelAgency } from './../../shared/utilities/whiteLabel.function'

import './EnrollmentLinkCreator.component.scss'

let debounceTimer
const debounce = async (func, delay) => {
  if (debounceTimer) {
    window.clearTimeout(debounceTimer)
    debounceTimer = null
  }

  debounceTimer = setTimeout(() => {
    func()
    window.clearTimeout(debounceTimer)
    debounceTimer = null
  }, delay)
}

const searchRecruiters = async query => {
  try {
    const is_team_recruiter = UserProfileService.get('can_recruit'),
      current_user_id = UserProfileService.get('id'),
      response = await AdminApi.getLimitedDownlineLeadersId(UserProfileService.get('id'), { query })

    let leaders = response?.data?.data || []
    leaders = is_team_recruiter ? leaders.filter(leader => leader.id !== current_user_id) : leaders

    return leaders.sort((a, b) =>
      `${[a.u_fname, a.u_lname].join(' ').trim()}`.localeCompare(`${[b.u_fname, b.u_lname].join(' ').trim()}`, 'en')
    )
  } catch (error) {
    console.error('Error fetching downline leaders:', error)
    toast.error('Failed to load recruiter list')
    return []
  }
}

const searchUplines = async query => {
  try {
    const response = await AdminApi.getLimitedLeadersByUplineId(
      UserProfileService.get('usertype_id') === 35 ? 1289 : UserProfileService.get('u_upline_id'),
      { query }
    )
    let leaders = response?.data?.data || []

    return leaders.sort((a, b) =>
      `${[a.u_fname, a.u_lname].join(' ').trim()}`.localeCompare(`${[b.u_fname, b.u_lname].join(' ').trim()}`, 'en')
    )
  } catch (error) {
    console.error('Error fetching upline leaders:', error)
    toast.error('Failed to load upline list')
    return []
  }
}

const getCommissionLabel = s => (isWhiteLabelAgency() ? `${s.com_name}` : `${s.com_name} (${s.com_code})`)

const getCommissionOptions = (commissionLevelsList, commission_id) => {
  const commId = parseInt(commission_id)
  const commission_levels = [
    commissionLevels.agent,
    commissionLevels.seniorBenefitsCounselor,
    commissionLevels.benefitsAdvisor,
    commissionLevels.benefitsCounselor,
    commissionLevels.loa,
  ].concat(
    [130, 213].includes(commId)
      ? [commissionLevels.region, commissionLevels.district]
      : commId === 129
      ? [commissionLevels.district]
      : []
  )

  if (UserProfileService.isA('agency-owner')) {
    commission_levels.push(commissionLevels.region)
    commission_levels.push(commissionLevels.district)
    commission_levels.push(commissionLevels.division)
  }

  return commissionLevelsList
    .filter(s => commission_levels.includes(s.com_code))
    .map(s => ({ value: `${s.id}`, text: getCommissionLabel(s) }))
}

class MgrStore {
  isVisitingLink = false
  isEmailingLink = false
  isCopyingLink = false

  isTeamRecruiter = false
  searchTerm = ''
  commLevels = null
  agentCommLevels = null

  commission = null // the selected commission level.
  commissionList = null
  commissionLevelList = null

  recruiter = null // the selected recruiter.
  recruiterList = null

  upline = null // the selected upline.
  uplineList = null

  enrollmentLink = false
  recipientEmailAddress = null

  // When true, generated link gets `&show_affiliate=1` appended so the
  // enrollment package selector includes the Affiliate Agent option.
  showAffiliate = false

  isLoading = {
    recruiters: false,
    commLevels: false,
    uplines: false,
  }

  constructor() {
    makeAutoObservable(this)
  }

  initUser = async userId => {
    if (!userId) return (this.userId = false)
    if (userId && parseInt(userId) === parseInt(this.userId)) return false

    this.isTeamRecruiter = UserProfileService.get('can_recruit')

    this.fetchCommLevels()
  }

  fetchCommLevels = async () => {
    this.isLoading.commLevels = true

    try {
      const upline = (await AdminApi.getUserDataById(UserProfileService.get('u_upline_id')))?.data
      let commissionLevelsList = []

      try {
        commissionLevelsList = (await AdminApi.getCommissionLevelList())?.data?.data || []
      } catch (ex) {
        console.error('Error fetching commission levels:', ex)
      }

      this.commissionLevelList = commissionLevelsList
      upline.commission_id = upline?.data?.commission_id
      // console.log("upline", upline)

      const currentCommId = parseInt(UserProfileService.get('commission_id'))
      const commission_levels = [
        commissionLevels.agent,
        commissionLevels.seniorBenefitsCounselor,
        commissionLevels.benefitsAdvisor,
        commissionLevels.benefitsCounselor,
        commissionLevels.loa,
      ].concat(
        [130, 213].includes(currentCommId)
          ? [commissionLevels.region, commissionLevels.district]
          : currentCommId === 129
          ? [commissionLevels.district]
          : []
      )

      if (UserProfileService.isA('agency-owner')) {
        commission_levels.push(commissionLevels.region)
        commission_levels.push(commissionLevels.district)
        commission_levels.push(commissionLevels.division)
      }
      // console.log("Intial Load",commission_levels)

      const options = commissionLevelsList
        .filter(s => commission_levels.includes(s.com_code))
        .map(s => ({
          value: `${s.id}`,
          text: getCommissionLabel(s),
        }))

      // Agent's selectable commission levels.
      this.commissionList = options || []
      return commissionLevelsList
    } catch (error) {
      console.error('Error fetching commissions:', error)
      toast.error('Failed to load commission levels')
    } finally {
      this.isLoading.commLevels = false
    }
  }

  updateEnrollmentLink = opts => {
    let { recruiterId, uplineId, comCode } = (opts = opts && typeof opts === 'object' ? opts : {})

    recruiterId = recruiterId ? recruiterId : this.recruiter?.value
    uplineId = uplineId ? uplineId : this.upline?.value
    comCode = comCode ? comCode : this.commission?.value || this.commission

    this.enrollmentLink = buildEnrollmentUrl({
      uLogin: UserProfileService.get('u_login'),
      recruiterId,
      uplineId,
      comCode,
      showAffiliate: this.showAffiliate,
    })
  }

  // Flipping the toggle immediately rebuilds `enrollmentLink`.
  setShowAffiliate = value => {
    this.showAffiliate = !!value
    this.updateEnrollmentLink()
  }

  searchRecruiters = async searchTerm => {
    this.isLoading.recruiters = true
    this.recruiterList = await searchRecruiters((this.searchTerm = searchTerm))
    this.isLoading.recruiters = false
  }

  onRecruiterChange = async selected => {
    // refactor: handleRecruiterChange
    if (!selected) return false

    const { value } = selected || {}
    if (!value) return false

    this.recruiter = selected

    let recruiter = this.recruiterList && this.recruiterList.find(item => parseInt(item.id) === parseInt(value))
    if (recruiter)
      this.commissionList = getCommissionOptions(this.commissionLevelList || [], recruiter.commission_id) || []

    // Is the commission in the avail list?
    // If the currently selected commission code is not permitted under the newly selected agent - clear the comm code out.
    let commCode = this.commission && !isNaN(this.commission) ? parseInt(this.commission) : null
    commCode = commCode && !~(this.commissionList || []).map(i => parseInt(i.value)).indexOf(commCode) ? null : commCode
    this.commission = commCode

    this.updateEnrollmentLink()
  }

  onCommissionChange = async com_code => {
    this.commission = com_code

    if (!com_code) return false

    this.updateEnrollmentLink({
      recruiterId: this.recruiter?.value ? this.recruiter?.value : !this.isTeamRecruiter ? this.userId : '',
    })
  }

  searchUplines = async searchTerm => {
    this.isLoading.uplines = true
    this.uplineList = await searchUplines((this.searchTerm = searchTerm))
    this.isLoading.uplines = false
  }

  onUplineChange = async selected => {
    // refactor: handleUplineChange

    if (!selected) return false

    const { value } = selected || {}
    if (!value) return false

    this.upline = selected

    const upline = this.uplineList.find(item => parseInt(item.id) === parseInt(value))
    if (upline) {
      const uplineCommId = parseInt(upline?.commission_id)
      const commission_levels = [
        commissionLevels.agent,
        commissionLevels.seniorBenefitsCounselor,
        commissionLevels.benefitsAdvisor,
        commissionLevels.benefitsCounselor,
        commissionLevels.loa,
      ].concat(
        [130, 213].includes(uplineCommId)
          ? [commissionLevels.region, commissionLevels.district]
          : uplineCommId === 129
          ? [commissionLevels.district]
          : []
      )

      // Agency owners (currently only "USA Benefits Group", id 1289) may also assign
      // Region/District/Division. Additive only — mirrors getCommissionOptions /
      // fetchCommLevels; the upline-comm-code logic above is left unchanged.
      if (UserProfileService.isA(['agency-owner'])) {
        commission_levels.push(commissionLevels.region)
        commission_levels.push(commissionLevels.district)
        commission_levels.push(commissionLevels.division)
      }

      const options = (this.commissionLevelList || [])
        .filter(s => commission_levels.includes(s.com_code))
        .map(s => ({
          value: `${s.id}`,
          text: getCommissionLabel(s),
        }))
      if (options) {
        this.commissionList = options || []
      }
    }

    // Is the commission in the avail list?
    // If the currently selected commission code is not permitted under the newly selected agent - clear the comm code out.
    let commCode = this.commission && !isNaN(this.commission) ? parseInt(this.commission) : null
    commCode = commCode && !~(this.commissionList || []).map(i => parseInt(i.value)).indexOf(commCode) ? null : commCode
    this.commission = commCode

    this.updateEnrollmentLink()
  }

  visitLink = async () => {
    this.isVisitingLink = true
    AuthService.logout().finally(() => {
      window.open(this.enrollmentLink)
      this.isVisitingLink = false
    })
  }

  copyLink = async () => {
    if (this.enrollmentLink) {
      navigator.clipboard.writeText(this.enrollmentLink)
      toast.success('Link copied to clipboard')
    }
  }

  emailLink = async () => {
    if (!this.recipientEmailAddress) {
      toast.error('Please enter an email address')
      return
    }

    if (this.enrollmentLink) {
      NotificationService.email({
        email_id: 289,
        data: {
          email_to: this.recipientEmailAddress,
          email_cc: UserProfileService.get('u_email'),
          enrollment_link: this.enrollmentLink,
        },
      }).then(
        () => {
          toast.success(`Enrollment email sent to ${this.recipientEmailAddress}`)
        },
        err => {
          console.error(`Failed to send enrollment link email. ${err}`)
        }
      )
    }
  }
}

const Store = new MgrStore()

const EnrollmentLinkCreator = () => {
  const userId = UserProfileService.getUserId(),
    {
      commission,
      commissionList,
      onCommissionChange,

      recruiter,
      recruiterList,
      searchRecruiters,
      onRecruiterChange,

      upline,
      uplineList,
      searchUplines,
      onUplineChange,

      initUser,
      isTeamRecruiter,
      enrollmentLink,
      recipientEmailAddress,
      updateEnrollmentLink,

      isLoading,
      isVisitingLink,
      visitLink,
      isCopyingLink,
      copyLink,
      isEmailingLink,
      emailLink,
    } = Store

  useEffect(() => {
    initUser(userId)
  }, [userId, initUser])

  useEffect(() => {
    if (enrollmentLink === false) updateEnrollmentLink()
  }, [enrollmentLink, updateEnrollmentLink])

  return (
    <MDBContainer id='EnrollmentLinkCreatorComponent'>
      <MDBRow>
        <MDBCol lg='4' className='elc-agent-typeahead'>
          {!isTeamRecruiter ? (
            <UITypeAhead
              isSearching={isLoading.recruiters}
              placeholder='Search Recruiter'
              onSearchChange={query => debounce(async () => searchRecruiters(query), 300)}
              onSelect={selected => onRecruiterChange(selected)}
              options={(recruiterList || []).map(L => ({
                value: L.id,
                text: [L.u_fname, L.u_lname].join(' ').trim(),
              }))}
              value={recruiter}
              required={true}
              rules={{ required: true }}
              showValidity={false}
              onValidityChange={validity => {}}
            ></UITypeAhead>
          ) : (
            <></>
          )}

          {isTeamRecruiter ? (
            <UITypeAhead
              isSearching={isLoading.uplines}
              placeholder='Search Upline'
              onSearchChange={query => debounce(async () => searchUplines(query), 300)}
              onSelect={selected => onUplineChange(selected)}
              options={(uplineList || []).map(L => ({
                value: L.id,
                text: [L.u_fname, L.u_lname].join(' ').trim(),
              }))}
              value={upline}
              required={true}
              rules={{ required: true }}
              showValidity={false}
              onValidityChange={validity => {}}
            ></UITypeAhead>
          ) : (
            <></>
          )}
        </MDBCol>

        <MDBCol lg='4' className='elc-comm-typeahead'>
          <UISelect
            label='Commission Level'
            disabled={isLoading.commLevels || (commissionList || []).length === 0}
            options={(commissionList || []).map(item => ({
              text: item.text,
              value: item.value,
              checked: item.value === commission,
            }))}
            search
            name='com_code'
            value={commission || 'Select Commission Level'}
            selected={commission || 'Select Commission Level'}
            getValue={option => {
              if (option?.value !== commission) {
                onCommissionChange(option?.value)
              }
            }}
          />
        </MDBCol>
        {isWhiteLabelAgency() ? (
          <></>
        ) : (
          <MDBCol lg='4' className='elc-show-affiliate'>
            <label
              className='elc-toggle-label'
              title='When on, the generated link includes the Affiliate Agent package on the enrollment screen.'
            >
              Show Affiliate Agent Option
            </label>
            <UIInputToggleSwitch
              width={90}
              labelOn='Yes'
              labelOff='No'
              value={Store.showAffiliate}
              onChange={value => Store.setShowAffiliate(value)}
            />
          </MDBCol>
        )}
      </MDBRow>
      <MDBRow>
        <MDBCol lg='12' className='elc-link'>
          <div>
            <UIInput type='text' value={enrollmentLink} readOnly={true} className='form-control' />
          </div>
        </MDBCol>
      </MDBRow>
      <MDBRow>
        <MDBCol size='12' lg='5' className='elc-btns'>
          <MDBBtnGroup>
            <MDBBtn color='danger' size='sm' disabled={!enrollmentLink} onClick={() => copyLink()}>
              {isCopyingLink ? 'Copying ...' : 'Copy Link'}
            </MDBBtn>
            <MDBBtn color='secondary' size='sm' disabled={!enrollmentLink} onClick={() => visitLink()}>
              {isVisitingLink ? 'Opening ...' : 'Visit Link'}
            </MDBBtn>
          </MDBBtnGroup>
        </MDBCol>

        <MDBCol size='12' lg='7' className='elc-send-link'>
          <div>
            <UIInput
              label='Email Address'
              value={recipientEmailAddress}
              onChange={e => (Store.recipientEmailAddress = e.target.value || '')}
              className='mr-2 flex-grow-1'
            />
          </div>
          <div>
            <MDBBtn color='primary' size='sm' disabled={!enrollmentLink} onClick={() => emailLink()}>
              {isEmailingLink ? 'Emailing ...' : 'Email Link'}
            </MDBBtn>
          </div>
        </MDBCol>
      </MDBRow>
    </MDBContainer>
  )
}

export default observer(EnrollmentLinkCreator)
